import { Page } from "../../../components/page-base";

import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { RowActionButtons } from "../../../components/datagrid/RowActionButtons";
import { NameCell } from "../../../components/datagrid/NameCell";
import { StatusChip } from "../../../components/datagrid/StatusChip";
import { TrendIndicator } from "../../../components/datagrid/TrendIndicator";
import { ToggleFollowCell } from "../../../components/datagrid/ToggleFollowCell";
import {
  BulkActionsBar,
  BulkAction,
} from "../../../components/datagrid/BulkActionsBar";
import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import PhonelinkOffIcon from "@mui/icons-material/PhonelinkOff";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SearchIcon from "@mui/icons-material/Search";
import {
  FilterDrawer,
  ChipFilterConfig,
  SearchFilterConfig,
} from "../../../components/filter-drawer";
import { Container } from "./styles";
import { isMobile } from "is-mobile";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
// import StarBorderIcon from "@mui/icons-material/StarBorder";
// import StarIcon from "@mui/icons-material/Star";
import {
  ProfileDetailsDialog,
  type ReportType,
} from "../components/profile-details-modal";
import { usePermission } from "../../../hooks/use-permission";
import { USER_TYPES } from "../../../permissions";
import {
  RadarSyncDialog,
  type RadarSyncScope,
} from "../components/radar-sync-dialog";
import { FollowingProfilesService } from "../services";
import { CustomersService } from "../../clients/services";
import useUserStore from "../../../store/user";
import { toast } from "react-toastify";
import { EditProfileModal } from "../../profiles-tracking/components/edit-profile-modal";
import { profilesTrackingService } from "../../profiles-tracking/services";
import { buildFollowingProfileUpdateData } from "./following-profile-save-payload";
import { buildProfileForModal } from "./following-profile-modal-data";
import { handleError } from "../../../utils/parseApiReturn";
import { KeywordSyncStatus } from "../components/keyword-sync-status";
import { PROFILE_POLL_INTERVAL_MS, shouldPollProfiles } from "./profile-polling";

export interface Profile {
  profileBusinessId: string;
  razaoSocial: string;
  locatidade: string;
  email: string;
  updatedAt: string;
  profileStatus: "ATENCAO" | "CRITICO" | "OK" | "null";
  rankedKeywords: number;
  totalKeywords: number;
  pendingKeywords: number;
  syncingKeywords: number;
  noResultKeywords: number;
  failedKeywords: number;
  syncing: boolean;
  businessProfileId?: number;
  suspendedAiReviewReplies?: boolean;
  allowAiCommentResponse: boolean;
  suspendedPosts?: boolean;
  allowPosts: boolean;
  tracking: boolean;
  isFavorite: boolean;
  customerStatus?: "disabled" | "success" | "warning";
  statusLabel?: "Ativo" | "Suspenso" | "Cópia";
}

interface ProfileForModal {
  id: string;
  name: string;
  category: string;
  status: "active" | "pending" | "paused" | "preparing";
  stats: {
    views: number;
    clicks: number;
    ranking: number;
  };
  shortName?: string;
  openingDate?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  description?: string;
  hours?: {
    monday?: string | string[];
    tuesday?: string | string[];
    wednesday?: string | string[];
    thursday?: string | string[];
    friday?: string | string[];
    saturday?: string | string[];
    sunday?: string | string[];
  };
}

const initialFilters = {
  profileStatus: "",
  ranked: "",
  localidade: "",
};

export const FollowingProfiles = () => {
  const { user } = useUserStore();
  const { hasRole } = usePermission();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [profileForModal, setProfileForModal] =
    useState<ProfileForModal | null>(null);
  const [profileDetailsReady, setProfileDetailsReady] = useState(false);
  const [profilesOnSync, setProfilesOnSync] = useState<string[]>([]);
  const [radarSyncProfile, setRadarSyncProfile] = useState<Profile | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasLoadedProfiles = useRef(false);

  type SortDirection = "asc" | "desc" | null;
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSortRankedKeywords = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? "asc" : prev === "desc" ? "asc" : null
    );
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  // Efeito para buscar dados sempre que a paginação, filtros ou busca mudarem
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.token) {
        toast.warn("Sessão expirada. Faça login novamente.");
        setLoading(false);
        return;
      }

      setLoading(!hasLoadedProfiles.current);
      try {
        const params = {
          token: user.token,
          page: pagination.page - 1,
          size: pagination.pageSize,
          query: searchTerm,
          profileStatus:
            filters.profileStatus === "" ? undefined : filters.profileStatus,
          ranking: filters.ranked === "" ? undefined : filters.ranked,
          localidade: filters.localidade ?? undefined,
          ...(sortDirection && { sort: `${sortDirection}` }),
        };

        const response =
          await FollowingProfilesService.getFollowingProfiles(params);
        if (response && response.content) {
          const mappedProfiles: Profile[] = response.content.map(
            (p: Profile) => ({
              profileBusinessId: p.profileBusinessId,
              razaoSocial: p.razaoSocial,
              locatidade: p.locatidade,
              email: p.email,
              updatedAt: p.updatedAt
                ? new Date(p.updatedAt).toLocaleDateString("pt-BR")
                : "-",
              profileStatus: p.profileStatus ?? "null",
              rankedKeywords: p.rankedKeywords,
              totalKeywords: p.totalKeywords,
              pendingKeywords: p.pendingKeywords ?? 0,
              syncingKeywords: p.syncingKeywords ?? 0,
              noResultKeywords: p.noResultKeywords ?? 0,
              failedKeywords: p.failedKeywords ?? 0,
              syncing: p.syncing ?? false,
              businessProfileId: p.businessProfileId,
              allowAiCommentResponse: !p.suspendedAiReviewReplies,
              allowPosts: !p.suspendedPosts,
              tracking: p.tracking,
              isFavorite: false,
            }),
          );
          setProfiles(mappedProfiles);
          setPagination((prev) => ({
            ...prev,
            totalCount: response.totalElements,
          }));
        } else {
          setProfiles([]);
          setPagination((prev) => ({ ...prev, totalCount: 0 }));
        }
      } catch (error) {
        console.error("Erro ao buscar perfis:", error);
        toast.error("Falha ao carregar perfis. Tente novamente.");
        setProfiles([]);
      } finally {
        hasLoadedProfiles.current = true;
        setLoading(false);
      }
    };

    fetchData();
  }, [pagination.page, pagination.pageSize, filters, searchTerm, user?.token, sortDirection, refreshKey]);

  useEffect(() => {
    if (!shouldPollProfiles(profiles, document.visibilityState)) return;
    const interval = window.setInterval(() => {
      if (shouldPollProfiles(profiles, document.visibilityState)) {
        setRefreshKey((value) => value + 1);
      }
    }, PROFILE_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [profiles]);

  const handleEdit = useCallback(
    async (id: string) => {
      const profile = profiles.find((p) => p.profileBusinessId === id);
      if (profile) {
        setSelectedProfile(profile);
        setProfileForModal(null);
        setProfileDetailsReady(false);
        setDetailsOpen(false);
        setEditModalOpen(false);
        try {
          const completeDetails =
            await profilesTrackingService.getProfileBusinessDetails(
              profile.profileBusinessId.toString(),
            );
          setProfileForModal(buildProfileForModal(profile, completeDetails));
          setProfileDetailsReady(true);
          setEditModalOpen(true);
        } catch (error) {
          console.error("Erro ao carregar detalhes completos do perfil:", error);
          setSelectedProfile(null);
          toast.warn(
            "Nao foi possivel carregar os dados completos. A edicao nao foi aberta para evitar perda de informacoes.",
          );
        }
        return;
      }
    },
    [profiles],
  );

  const handleSaveProfile = useCallback(
    async (updatedProfile: ProfileForModal) => {
      console.log("FOLLOWING-PROFILES handleSaveProfile called");
      if (!user?.token) {
        toast.warn("Sessão expirada. Faça login novamente.");
        return;
      }

      if (!selectedProfile) return;
      if (!profileDetailsReady || !profileForModal) {
        toast.warn("Aguarde o carregamento completo dos dados antes de salvar.");
        return;
      }

      try {
        await profilesTrackingService.updateProfile(
          selectedProfile.profileBusinessId,
          buildFollowingProfileUpdateData(profileForModal, updatedProfile),
        );

        // Atualizar a lista de perfis
        setProfiles((prev) =>
          prev.map((p) =>
            p.profileBusinessId === selectedProfile.profileBusinessId
              ? {
                  ...p,
                  razaoSocial: updatedProfile.name,
                  locatidade: updatedProfile.city || "",
                  email: updatedProfile.email || "",
                }
              : p,
          ),
        );
        setEditModalOpen(false);
        setSelectedProfile(null);
        toast.success("Perfil atualizado com sucesso!");
      } catch (error) {
        console.error("Erro ao salvar perfil:", error);
        const { message } =
          error instanceof Error
            ? { message: error.message }
            : handleError(error, "Erro ao salvar alteracoes do perfil.");
        toast.error(message);
        throw new Error(message);
      }
    },
    [user?.token, selectedProfile, profileForModal, profileDetailsReady],
  );

  // const handleFavorite = (id: number) => {
  //   console.log(`Salvando status de favorito para o perfil ${id} no banco...`);
  //   setProfiles((prev) =>
  //     prev.map((p) => (p.profileBusinessId === id ? { ...p, isFavorite: !p.isFavorite } : p))
  //   );
  // };

  const handleNameClick = (profile: Profile) => {
    setSelectedProfile(profile);
    setDetailsOpen(true);
  };

  const handleDisableFollow = async (id: string) => {
    await handleToggleFollow(id, false);
    setDetailsOpen(false);
  };

  const handleToggleFollow = useCallback(async (id: string, checked: boolean) => {
    if (!user?.token) return;

    // Otimistic update
    setProfiles((prev) =>
      prev.map((p) =>
        p.profileBusinessId === id ? { ...p, tracking: checked } : p,
      ),
    );

    try {
      await FollowingProfilesService.toggleTracking(Number(id), user.token);
    } catch {
      // Rollback
      setProfiles((prev) =>
        prev.map((p) =>
          p.profileBusinessId === id ? { ...p, tracking: !checked } : p,
        ),
      );
    }
  }, [user?.token]);

  const handleSyncKeywords = useCallback(
    async (
      profileId: string,
      client: "radar" | "serp_api",
      scope?: RadarSyncScope,
    ) => {
      try {
        setProfilesOnSync((prev) => [...prev, profileId]);
        await CustomersService.syncKeywords({
          businessProfileId: profileId,
          client,
          scope,
          token: user?.token || "",
        });
        setRefreshKey((value) => value + 1);
      } catch (ignorable) {
        console.error(ignorable);
      } finally {
        setProfilesOnSync((prev) => prev.filter((id) => id !== profileId));
      }
    },
    [user?.token],
  );

  const handleSwitchAiCommentResponse = useCallback(
    async (profileId: string, allowAiCommentResponse: boolean) => {
      try {
        setProfilesOnSync((prev) => [...prev, profileId]);
        await CustomersService.switchAiCommentResponse({
          businessProfileId: profileId,
          allowAiCommentResponse,
          token: user?.token || "",
        });
        setProfiles((prev) =>
          prev.map((p) =>
            p.profileBusinessId === profileId
              ? { ...p, allowAiCommentResponse: !p.allowAiCommentResponse }
              : p,
          ),
        );
      } catch (ignorable) {
        console.error(ignorable);
      } finally {
        setProfilesOnSync((prev) => prev.filter((id) => id !== profileId));
      }
    },
    [user?.token],
  );

  const handleSwitchPosts = useCallback(
    async (profileId: string, currentlyAllowed: boolean) => {
      try {
        setProfilesOnSync((prev) => [...prev, profileId]);
        await CustomersService.switchPostsSuspension({
          businessProfileId: profileId,
          suspendPosts: currentlyAllowed,
          token: user?.token || "",
        });
        setProfiles((prev) =>
          prev.map((p) =>
            p.profileBusinessId === profileId
              ? { ...p, allowPosts: !p.allowPosts }
              : p,
          ),
        );
      } catch (ignorable) {
        console.error(ignorable);
      } finally {
        setProfilesOnSync((prev) => prev.filter((id) => id !== profileId));
      }
    },
    [user?.token],
  );

  const handleSelectRow = (rowId: string | number) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRowIds(checked ? profiles.map((p) => p.profileBusinessId) : []);
  };

  const handleBulkToggleFollow = () => {
    setProfiles((prev) =>
      prev.map((p) =>
        selectedRowIds.includes(p.profileBusinessId)
          ? { ...p, following: !p.tracking }
          : p,
      ),
    );
    setSelectedRowIds([]);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    // Aqui você chamaria a API para buscar os dados filtrados
    console.log(`Filtering by ${field}: ${value}`);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    // Aqui você chamaria a API para buscar os dados sem filtros
    console.log("Clearing filters");
  };

  const handleSearchLocation = (query: string) => {
    // Aqui você chamaria a API para buscar por localidade
    console.log("Searching location in DB:", query);
  };

  const chipFilters: ChipFilterConfig[] = [
    {
      id: "profileStatus",
      label: "Status",
      options: [
        { label: "Todos", value: "" },
        { label: "Ok", value: "OK" },
        { label: "Atenção", value: "ATENCAO" },
        { label: "Crítico", value: "CRITICO" },
      ],
    },
    {
      id: "ranked",
      label: "Palavras Ranqueadas",
      options: [
        { label: "Todas", value: "" },
        { label: "Zero", value: "zero" },
        { label: "1-7", value: "1-7" },
        { label: "8+", value: "8+" },
      ],
    },
  ];

  const searchFilters: SearchFilterConfig[] = [
    {
      id: "location",
      label: "Localidade",
      placeholder: "Buscar por localidade",
      onSearch: handleSearchLocation,
    },
  ];

  const columns: DataGridColumn[] = [
    { id: "actions", header: "Ações", minWidth: 120 },
    { id: "name", header: "Nome", minWidth: 170 },
    { id: "location", header: "Localidade", minWidth: 150 },
    { id: "email", header: "Email", minWidth: 200, hideOnMobile: true },
    {
      id: "lastUpdate",
      header: "Última atualização",
      minWidth: 150,
      hideOnMobile: true,
    },
    { id: "status", header: "Status" },
    { id: "sync", header: "Sincronização", minWidth: 170 },
    { id: "totalKw", header: "Total KW" },
    {
      id: "ranked",
      header: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Ranqueadas
          <Tooltip
            title={
              sortDirection === null
                ? "Ordenar decrescente"
                : sortDirection === "desc"
                ? "Ordenar crescente"
                : "Remover ordenação"
            }
          >
            <IconButton size="small" onClick={handleSortRankedKeywords}>
              {sortDirection === "desc" ? (
                <ArrowDownwardIcon fontSize="inherit" />
              ) : sortDirection === "asc" ? (
                <ArrowUpwardIcon fontSize="inherit" />
              ) : (
                <UnfoldMoreIcon fontSize="inherit" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { id: "following", header: "Acompanhamento" },
  ];

  const rows: DataGridRow[] = useMemo(() => {
    return profiles.map((profile) => {
      const canSyncProfile =
          typeof profile.profileBusinessId === "string" &&
          profile.profileBusinessId.length > 0;
        const businessProfileId = profile.profileBusinessId ?? "";
      const syncDisabled =
        !canSyncProfile || profilesOnSync.includes(businessProfileId);

      return {
        id: profile.profileBusinessId,
        render: {
          actions: (
            <RowActionButtons
              actions={[
                {
                  icon: <ModeEditOutlineIcon fontSize="small" />,
                  tooltip: "Editar",
                  onClick: () => handleEdit(profile.profileBusinessId),
                },
                {
                  icon: (
                    <PostAddIcon
                      fontSize="small"
                      color={profile.allowPosts ? "success" : "error"}
                    />
                  ),
                  disabled: syncDisabled,
                  tooltip: profile.allowPosts
                    ? "Postagens automáticas ATIVAS — clique para pausar"
                    : "Postagens automáticas PAUSADAS — clique para reativar",
                  onClick: () =>
                    handleSwitchPosts(businessProfileId, profile.allowPosts),
                },
                {
                  icon: (
                    <PhonelinkOffIcon
                      fontSize="small"
                      color={
                        profile.allowAiCommentResponse ? "success" : "error"
                      }
                    />
                  ),
                  disabled: syncDisabled,
                  tooltip: profile.allowAiCommentResponse
                    ? "Respostas de IA nas avaliações ATIVAS — clique para pausar"
                    : "Respostas de IA nas avaliações PAUSADAS — clique para reativar",
                  onClick: () =>
                    handleSwitchAiCommentResponse(
                      businessProfileId,
                      profile.allowAiCommentResponse,
                    ),
                },
                {
                  icon: <CachedOutlinedIcon fontSize="small" color="error" />,
                  disabled: syncDisabled,
                  tooltip: "Sincronizar palavras-chave via Radar",
                  onClick: () => setRadarSyncProfile(profile),
                },
                {
                  icon: (
                    <CachedOutlinedIcon fontSize="small" color="success" />
                  ),
                  disabled: syncDisabled,
                  tooltip: "Sincronizar palavras-chave via SerpAPI",
                  onClick: () =>
                    handleSyncKeywords(businessProfileId, "serp_api"),
                },
                // {
                //   icon: profile.isFavorite ? (
                //     <StarIcon sx={{ color: "#FBBC05" }} fontSize="small" />
                //   ) : (
                //     <StarBorderIcon fontSize="small" />
                //   ),
                //   tooltip: profile.isFavorite ? "Desfavoritar" : "Favoritar",
                //   onClick: () => handleFavorite(profile.profileBusinessId),
                // },
              ]}
            />
          ),
          name: (
            <NameCell
              primaryText={profile.razaoSocial ?? "-"}
              onClick={() => handleNameClick(profile)}
            />
          ),
          location: (
            <Typography variant="body2" color="text.secondary">
              {profile.locatidade ?? "-"}
            </Typography>
          ),
          email: (
            <Typography variant="body2" color="text.secondary">
              {profile.email ?? "-"}
            </Typography>
          ),
          lastUpdate: (
            <Typography variant="body2" color="text.secondary">
              {profile.updatedAt}
            </Typography>
          ),
          status: <StatusChip status={profile.profileStatus} />,
          sync: (
            <KeywordSyncStatus
              pendingKeywords={profile.pendingKeywords}
              syncingKeywords={profile.syncingKeywords}
              noResultKeywords={profile.noResultKeywords}
              failedKeywords={profile.failedKeywords}
              totalKeywords={profile.totalKeywords}
            />
          ),
          totalKw: (
            <Typography variant="body2" color="text.secondary">
              {profile.totalKeywords}
            </Typography>
          ),
          ranked: <TrendIndicator value={profile.rankedKeywords} />,
          following: (
            <ToggleFollowCell
              checked={profile.tracking}
              onToggle={(checked) =>
                handleToggleFollow(profile.profileBusinessId, checked)
              }
            />
          ),
        },
      };
    });
  }, [
    profiles,
    profilesOnSync,
    handleEdit,
    handleSwitchAiCommentResponse,
    handleSwitchPosts,
    handleSyncKeywords,
    handleToggleFollow,
  ]);

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (filters.profileStatus && filters.profileStatus !== "") {
      chips.push({
        id: "profileStatus",
        label: `Status: ${filters.profileStatus}`,
        onDelete: () => handleFilterChange("profileStatus", ""),
      });
    }
    if (filters.ranked && filters.ranked !== "") {
      chips.push({
        id: "ranked",
        label: `Ranqueadas: ${filters.ranked}`,
        onDelete: () => handleFilterChange("ranked", ""),
      });
    }
    if (filters.localidade) {
      chips.push({
        id: "location",
        label: `Local: ${filters.localidade}`,
        onDelete: () => handleFilterChange("location", ""),
      });
    }
    return chips;
  }, [filters]);

  const handleViewDashboard = (profileId: string) => {
    window.open(`/dashboard/${profileId}`, "_blank");
  };

  const handleOpenReport = (profileId: string, tipo: ReportType) => {
    window.open(
      tipo === "winback"
        ? `/relatorio-winback/${profileId}`
        : `/relatorio/${profileId}`,
      "_blank"
    );
  };

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Perfis em acompanhamento"
          subtitle="Monitore e gerencie perfis com problemas de ranqueamento"
        />
        <Box
          sx={{ width: "88%", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}
        >
          <AppDataGrid
            columns={columns}
            rows={rows}
            tableLabel="perfis"
            selectedRowIds={selectedRowIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar por nome, localidade ou email..." // parâmetro
            filterChips={filterChips} // chips ativos
            filterCount={filterChips.length} // contador no botão
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            bulkActionsBarRenderer={(selectedIds) => {
              const bulkActions: BulkAction[] = [
                {
                  label: "Alternar Acompanhamento",
                  onClick: handleBulkToggleFollow,
                },
                {
                  label: "Cancelar",
                  onClick: () => setSelectedRowIds([]),
                  color: "secondary",
                },
              ];

              return (
                <BulkActionsBar
                  selectedCount={selectedIds.length}
                  tableLabel="perfil"
                  actions={bulkActions}
                />
              );
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhum perfil em acompanhamento"
            noDataDescription="Adicione perfis para começar o monitoramento"
            isLoading={loading}
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          chipFilters={chipFilters}
          searchFilters={searchFilters}
        />
        <ProfileDetailsDialog
          open={detailsOpen}
          profile={
            selectedProfile
              ? {
                  id: String(selectedProfile.profileBusinessId),
                  name: selectedProfile.razaoSocial,
                  location: selectedProfile.locatidade,
                  email: selectedProfile.email,
                  totalKw: selectedProfile.totalKeywords,
                  rankedValue: selectedProfile.rankedKeywords,
                  status: selectedProfile.customerStatus || "success",
                  statusLabel: selectedProfile.statusLabel || "Ativo",
                  lastUpdate: selectedProfile.updatedAt,
                  following: selectedProfile.tracking,
                }
              : null
          }
          onClose={() => setDetailsOpen(false)}
          onEdit={(id: string | number) => handleEdit(String(id))}
          onViewDetails={(id: string) => {
            handleViewDashboard(id);
          }}
          onDisableFollow={(id: string | number) =>
            handleDisableFollow(String(id))
          }
          showDisableFollow
          showEditAction
          onOpenReport={handleOpenReport}
          showReportActions={hasRole(USER_TYPES.ADMIN)}
        />
        <EditProfileModal
          isOpen={editModalOpen}
          profile={profileForModal}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedProfile(null);
            setProfileForModal(null);
          }}
          onSave={handleSaveProfile}
        />
        <RadarSyncDialog
          open={Boolean(radarSyncProfile)}
          profileName={radarSyncProfile?.razaoSocial ?? "este perfil"}
          loading={Boolean(
            radarSyncProfile &&
              profilesOnSync.includes(radarSyncProfile.profileBusinessId),
          )}
          onClose={() => setRadarSyncProfile(null)}
          onConfirm={async (scope) => {
            if (!radarSyncProfile) return;
            await handleSyncKeywords(
              radarSyncProfile.profileBusinessId,
              "radar",
              scope,
            );
            setRadarSyncProfile(null);
          }}
        />
      </Container>
    </Page.Root>
  );
};
